<template>
  <div
    :class="[
      'GambitCard',
      {
        'has-images': gambit.locationImageBlobHash,
        'image-loaded': isImageLoaded,
        'high-aspect': isHighAspectRatio,
        'is-mobile': isMobile,
        'is-joined': localIsAttending,
        'is-expired': isExpired,
      },
    ]"
  >
    <div class="cover" @click="showLocationActions = !showLocationActions">
      <div class="location-image">
        <img
          v-if="gambit.locationImageBlobHash"
          class="picture"
          :src="getBlobImage(gambit.locationImageBlobHash, 343)"
          loading="lazy"
          @load="handleImageLoaded"
        />
      </div>

      <div v-if="gambit.locationImageBlobHash && circleLocation" class="spot-marker">
        <LocationCircle :location="circleLocation" :selected="!isExpired" mode="none" />
      </div>

      <Transition name="location-actions">
        <div v-if="showLocationActions" class="location-actions-overlay" @click.stop="showLocationActions = false">
          <div class="location-action-btn" @click.stop="handleOpenGambit">
            <div class="info-icon">
              <GamIcon :name="GamIconName.TOOLTIP" size="30px" is-current />
              <span v-if="showUnreadChat" class="unread-badge">
                <GamIcon :name="GamIconName.MESSAGE" size="10px" is-current />
              </span>
            </div>
            <span>{{ localize('card.action.info') }}</span>
          </div>
          <div class="location-action-btn" @click.stop="handleLocationDetailsClick">
            <GamIcon :name="GamIconName.LOCATION" size="30px" is-current />
            <span>{{ localize('card.action.spot') }}</span>
          </div>
          <div class="location-action-btn" @click.stop="handleNavigateClick">
            <GamIcon :name="GamIconName.NAVIGATE" size="30px" is-current />
            <span>{{ localize('card.action.navigate') }}</span>
          </div>
        </div>
      </Transition>

      <div v-if="!isExpired && isPWA" class="cover-actions">
        <div class="card-action" @click.stop="handleShareGambit">
          <GamIcon :name="GamIconName.SHARE" size="17px" is-current />
        </div>
      </div>

      <div class="cover-foot">
        <div
          :class="['cover-players', { 'is-attendee-stack': isAttendeeStack }]"
          :key="playerSlots.filter((slot) => !!slot.player).length"
        >
          <template v-if="isAttendeeStack">
            <div v-if="stackedAttendees.length || stackOverflowCount" class="attendee-pile">
              <div
                v-for="(attendee, a) in stackedAttendees"
                class="seat is-piled"
                :style="{ zIndex: a + 1 }"
                :key="`pile-${attendee.id || attendee.username || a}`"
              >
                <PlayerAvatar
                  :player="attendee"
                  is-confirmed
                  is-overlapping
                  hide-rating
                  :key="`avatar-${attendee.username}`"
                  @click.stop="handleAvatarClick(attendee)"
                />
              </div>

              <div v-if="stackOverflowCount" class="seat is-piled" :style="{ zIndex: stackedAttendees.length + 1 }">
                <div class="seat-ring is-more" @click.stop="handleOpenGambit">
                  <span class="seat-count">+{{ stackOverflowCount }}</span>
                </div>
              </div>
            </div>

            <div class="seat is-invite-apart">
              <div class="seat-ring is-invite" @click.stop="handleOpenInvitePlayers">
                <span v-if="myGambitInviteeCount" class="seat-count">{{ myGambitInviteeCount }}</span>
                <GamIcon v-else :name="GamIconName.PLUS" size="20px" is-current />
              </div>
            </div>
          </template>

          <template v-else>
            <div
              v-for="(slot, a) in playerSlots"
              :class="['seat', { 'is-unconfirmed': isUnconfirmedChallenge && !slot.player }]"
              :key="`player-${slot.player?.id || slot.player?.username || a}`"
            >
              <div
                v-if="a === playerSlots.length - 1 && !isUnconfirmedChallenge"
                class="seat-ring is-invite"
                @click.stop="handleOpenInvitePlayers"
              >
                <span v-if="myGambitInviteeCount" class="seat-count">{{ myGambitInviteeCount }}</span>
                <GamIcon v-else :name="GamIconName.PLUS" size="20px" is-current />
              </div>

              <div
                v-else-if="a === 2 && localAttendees.length > 2 && !slot.isInvited"
                class="seat-ring is-more"
                @click.stop="handleOpenGambit"
              >
                <span class="seat-count">+{{ localAttendees.length - 2 }}</span>
              </div>

              <PlayerAvatar
                v-else-if="slot.player"
                :player="slot.player"
                :is-confirmed="!slot.isInvited"
                :is-invited="slot.isInvited"
                is-stacked
                hide-rating
                :key="`avatar-${slot.player.username}`"
                @click.stop="handleAvatarClick(slot.player)"
              />

              <div v-else class="seat-ring" />
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="chip-band">
      <span v-if="displayTitle" class="band-title">{{ displayTitle }}</span>
      <GamChip v-for="(tag, index) in gambitBandTags" v-bind="tag" :key="`gambit-band-${index}`" />
    </div>

    <div class="meta" :class="{ 'needs-respond': needsRespond }" @click="dismissLocationActions">
      <div class="meta-head">
        <div class="meta-head-text">
          <div class="when">
            <span v-if="gambitDateType === GamDateType.NOW" class="day day-live">NOW</span>
            <span v-else-if="gambitDateType === GamDateType.SOON" class="day day-live">SOON</span>
            <span v-else-if="isIntentToday" class="day day-live">Today</span>
            <span v-else-if="isIntentTomorrow" class="day day-live">Tomorrow</span>
            <span v-else class="day">{{ formatInTimeZone(gambit.start, gambit.timezone || 'UTC', 'EEEE') }}</span>
            <span class="sep">&middot;</span>
            <span class="time">{{ formatInTimeZone(gambit.start, gambit.timezone || 'UTC', 'HH:mm') }}</span>
          </div>

          <div class="sub">
            <span
              v-if="gambit.location"
              :class="['spot', { confirmed: gambit.isConfirmed && !hasPendingRequestInvitation }]"
              @click.stop="showLocationActions = !showLocationActions"
            >
              {{ gambit.location.name }}
            </span>

            <template v-if="gambit.club">
              <span class="sep">&middot;</span>
              <span class="club-link" @click.stop="handleNavigateToClub">@{{ gambit.club.username }}</span>
            </template>

            <template v-if="statusLabel">
              <span class="sep">&middot;</span>
              <span :class="['status', { confirmed: isConfirmedStatus }]">
                <GamIcon v-if="isConfirmedStatus" :name="GamIconName.CHECK" size="10px" is-current />
                {{ statusLabel }}
              </span>
            </template>
          </div>

          <div v-if="conflictingGambit" class="conflict-warning">
            <GamIcon :name="GamIconName.WARNING" size="12px" is-current />
            <span>{{
              localizeWithValues('sparks.conflict.attending', getAttendanceConflictLabel(conflictingGambit))
            }}</span>
          </div>
        </div>

        <div class="info-action" @click.stop="handleOpenGambit">
          <div class="info-icon">
            <GamIcon :name="GamIconName.TOOLTIP" size="24px" is-current />
            <span v-if="showUnreadChat" class="unread-badge">
              <GamIcon :name="GamIconName.MESSAGE" size="10px" is-current />
            </span>
          </div>
          <span>{{ localize('card.action.info') }}</span>
        </div>
      </div>

      <div class="controls">
        <div class="tertiary">
          <div
            v-if="showChessSetAction"
            :class="[
              'chess-set-action',
              'has-status',
              { 'is-on': myChessSetCount > 0, 'has-warning': isShortOfChessSets },
            ]"
            @click.stop="handleOpenChessSetPopover"
          >
            <GamIcon :name="GamIconName.CHESS_SET" size="20px" is-current />
          </div>
        </div>

        <div class="primary">
          <div v-if="isExpired" class="expired-notice">Expired</div>

          <div v-else-if="hasGambitConcluded" class="expired-notice">Concluded</div>

          <div v-else-if="isSentChallenge" class="expired-notice waiting-notice">{{ waitingForLabel }}</div>

          <GamButton
            v-else-if="localIsAttending"
            class="action icon-circle play-cta play-cta-gambit is-inverted"
            :variant="GamButtonVariant.SLAB"
            :size="GamButtonSize.LARGE"
            :left-icon="GamIconName.PAUSE"
            :is-disabled="isSubmitting"
            :is-loading="isSubmitting"
            @click="handleLeaveGambit"
            is-icon-only
          />

          <GamButton
            v-else-if="isUnconfirmedChallenge"
            class="action play-cta play-cta-gambit"
            :variant="GamButtonVariant.SLAB"
            :size="GamButtonSize.SMALL"
            :is-disabled="isSubmitting"
            :is-loading="isSubmitting"
            @click="handleJoinGambit"
          >
            Accept
          </GamButton>

          <GamButton
            v-else
            class="action icon-circle play-cta play-cta-gambit"
            :variant="GamButtonVariant.SLAB"
            :size="GamButtonSize.LARGE"
            :left-icon="GamIconName.PLAY_EMPTY"
            :is-disabled="isSubmitting"
            :is-loading="isSubmitting"
            @click="handleJoinGambit"
            is-icon-only
          />
        </div>

        <div class="secondary" />
      </div>
    </div>

    <GamChessSetPopover
      v-model:open="showChessSetPopover"
      v-model="editChessSetCount"
      :player-chess-sets="playerChessSets"
      :spot-chess-sets="spotChessSets"
      :is-loading="isUpdatingChessSet"
      @confirm="handleConfirmChessSet"
    />

    <LeaveGambitConfirmModal
      v-if="isLeaveConfirmOpen"
      :title="leaveConfirmTitle"
      @close="dismissLeaveConfirm"
      @keep="dismissLeaveConfirm"
      @confirm="confirmLeaveGambit"
    />
  </div>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { BaseGambitDto } from '@/core/data/gambit/gambit.interface';
import { GambitInvitationType } from '@/core/data/gambit/gambit.type';
import type { BaseLocationDto, NextGambitDto } from '@/core/data/location/location.interface';
import { LocationChessFeature } from '@/core/data/location/location.type';
import type { SparkIntent } from '@/core/data/spark/spark.interface';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import GambitHelper, { pluralize } from '@/core/helpers/gambit.helper';
import LocationHelper from '@/core/helpers/location.helper';
import PlayerHelper from '@/core/helpers/player.helper';
import { isMobile, isPWA } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useGambitStore } from '@/stores/gambit.store';
import { useMapStore } from '@/stores/map.store';
import { useSparkStore } from '@/stores/spark.store';
import LocationCircle from '@/views/components/gam-location/LocationCircle.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamChessSetPopover from '@/views/components/GamChessSetPopover.vue';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamDateType } from '@/views/composables/constants/components/gamGambit.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { useLeaveGambitConfirm } from '@/views/composables/hooks/useLeaveGambitConfirm';
import { useUnpairedChip } from '@/views/composables/hooks/useUnpairedChip';
import type { GambitSearchResultDto } from '@/views/composables/models/components/GambitSearchResultDto';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import InvitePlayers, { type ChosenPlayer } from '@/views/main/gambits/modals/InvitePlayers.vue';
import LeaveGambitConfirmModal from '@/views/main/gambits/modals/LeaveGambitConfirmModal.vue';
import PlayerAvatar from '@/views/main/sparks/components/PlayerAvatar.vue';
import { findAttendanceConflict, getAttendanceConflictLabel } from '@/views/main/sparks/utils/attendance-conflict';
import { getBlobImage } from '@/views/main/sparks/utils/image-blob';
import { addDays, isAfter, isSameDay, isToday, isTomorrow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

interface SparkMatchProps {
  intent: SparkIntent;
  gambit: GambitSearchResultDto | GambitDetailDto;
  now: Date;
  expiresAt: Date | string;
}

const props = defineProps<SparkMatchProps>();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const gambitStore = useGambitStore();
const sparkStore = useSparkStore();
const { myGambits } = storeToRefs(sparkStore);

const router = useRouter();
const route = useRoute();

// Refs

const isImageLoaded = ref<boolean>(false);
const isSubmitting = ref<boolean>(false);
const showLocationActions = ref(false);
const showChessSetPopover = ref(false);
const editChessSetCount = ref(0);
const isUpdatingChessSet = ref(false);

function dismissLocationActions() {
  if (showLocationActions.value) showLocationActions.value = false;
}

onBeforeUnmount(() => {
  window.removeEventListener('scroll', dismissLocationActions, true);
});

const localIsAttending = ref(props.gambit.isAttending);
const localAttendees = ref([...(props.gambit.attendees || [])]);
const localInvitations = ref([...(props.gambit.invitations || [])]);
const hasUnreadChat = ref(!!props.gambit.hasUnreadChatMessages);

watch(
  () => props.gambit,
  (next) => {
    if (!next) return;
    localIsAttending.value = next.isAttending;
    hasUnreadChat.value = !!next.hasUnreadChatMessages;
    if (next.attendees?.length) {
      localAttendees.value = [...next.attendees];
    }
    if (next.invitations) {
      localInvitations.value = [...next.invitations];
    }
  },
);

const conflictingGambit = computed(() => {
  if (isExpired.value || hasGambitConcluded.value) return null;

  return findAttendanceConflict({ start: props.gambit.start, end: props.gambit.end }, myGambits.value, [
    props.gambit.id,
  ]);
});

onMounted(async () => {
  window.addEventListener('scroll', dismissLocationActions, true);

  const hasCompleteAttendees = localAttendees.value.length > 0 && localAttendees.value.some((a) => !!a?.username);
  if (hasCompleteAttendees) return;
  const result = await gambits.gambitService.getGambit(props.gambit.id);
  if (result.data) {
    if (result.data.attendees?.length) {
      localAttendees.value = [...result.data.attendees];
    }
    if (result.data.isAttending !== undefined) {
      localIsAttending.value = result.data.isAttending;
    }
    if (result.data.hasUnreadChatMessages !== undefined) {
      hasUnreadChat.value = result.data.hasUnreadChatMessages;
    }
  }
});

function getInviteParticipantIds() {
  const attendeeIds = localAttendees.value.map((attendee) => attendee?.id).filter((id): id is string => !!id);
  const invitationIds = localInvitations.value.flatMap((invitation) => {
    if ('userId' in invitation && typeof invitation.userId === 'string') {
      return invitation.userId;
    }

    if ('userIds' in invitation && Array.isArray(invitation.userIds)) {
      return invitation.userIds.filter((id): id is string => typeof id === 'string');
    }

    return [];
  });

  return [...new Set([...attendeeIds, ...invitationIds])];
}

function getInviteInitialPlayers(): ChosenPlayer[] {
  const currentUserId = userDetails.value?.id;
  const players: ChosenPlayer[] = [];
  const seenIds = new Set<string>();

  for (const invitation of localInvitations.value) {
    if (getInvitationOwnerId(invitation) !== currentUserId) continue;
    const userId = getInvitationUserId(invitation);
    if (!userId || seenIds.has(userId)) continue;
    seenIds.add(userId);

    const user = 'user' in invitation ? invitation.user : undefined;
    players.push({
      id: userId,
      username: user?.username || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      rating: user?.rating || 0,
      profileImageBlobHash: user?.profileImageBlobHash ?? null,
    });
  }

  return players;
}

function getInviteModalAttrs() {
  return {
    gambitId: props.gambit.id,
    participantIds: getInviteParticipantIds(),
    initialPlayers: getInviteInitialPlayers(),
    onClose: handleCloseInvitePlayers,
  };
}

const {
  open: openInvitePlayers,
  close: closeInvitePlayers,
  patchOptions: patchInvitePlayersOptions,
} = useModal({
  component: InvitePlayers,
  attrs: getInviteModalAttrs(),
});

// Computed

const isExpired = computed(() => {
  const expirationDate = typeof props.expiresAt === 'string' ? new Date(props.expiresAt) : props.expiresAt;
  return isAfter(props.now, expirationDate);
});

const displayTitle = computed(() => {
  const title = props.gambit.title;
  if (!title) return null;
  const locName = props.gambit.location?.name;
  if (!locName) return title;
  if (title === locName) return null;
  if (title.startsWith(`${locName} at `)) return null;
  return title;
});

/* The cover is a static map centred on the spot, so it carries no pin of its own.
   Reusing the map's marker keeps the card and the map reading the same. */
const circleLocation = computed((): BaseLocationDto | null => {
  const loc = props.gambit.location as Partial<BaseLocationDto> | undefined;
  if (!loc?.id || !loc.coordinates || !loc.type) return null;

  return {
    id: loc.id,
    coordinates: loc.coordinates,
    type: loc.type,
    chessFeatures: loc.chessFeatures ?? [],
    generalFeatures: loc.generalFeatures ?? [],
    private: loc.private ?? false,
    favorite: loc.favorite ?? false,
    isVetted: loc.isVetted ?? false,
    name: loc.name ?? '',
    address: loc.address ?? '',
    timezone: loc.timezone,
    coverBlobHash: loc.coverBlobHash ?? null,
    activeChessboardCount: 0,
    /* Carrying this gambit marks the spot as active, which is what keeps its photo
       in colour rather than the "nothing upcoming" grey. */
    nextGambits: isExpired.value
      ? []
      : ([{ id: props.gambit.id, start: props.gambit.start }] as unknown as NextGambitDto[]),
  };
});

const gambitDateType = computed(() => {
  return GambitHelper.getGambitsDateType(
    typeof props.gambit.start === 'string' ? props.gambit.start : props.gambit.start.toString(),
    false,
    typeof props.gambit.end === 'string' ? props.gambit.end : props.gambit.end?.toString(),
    props.now,
  );
});

const isIntentToday = computed(() => isSameDay(props.intent.date, props.now));

const isIntentTomorrow = computed(() => isSameDay(props.intent.date, addDays(props.now, 1)));

const hasGambitConcluded = computed(() => {
  return new Date(props.gambit.end).getTime() < props.now.getTime();
});

const isHighAspectRatio = computed(() => {
  return false;
});

const avatarsCount = computed(() => {
  if (props.gambit.isChallenge && !props.gambit.isConfirmed) return 2;
  return 4;
});

const isConcluded = computed(() => {
  return new Date(props.gambit.end).getTime() < new Date().getTime();
});

const showUnreadChat = computed(
  () =>
    hasUnreadChat.value &&
    GambitHelper.isChatAccessible(
      {
        end: props.gambit.end,
        isChallenge: props.gambit.isChallenge,
        isConfirmed: props.gambit.isConfirmed,
        isAttending: localIsAttending.value,
      },
      props.now,
    ),
);

const prioritizedAttendees = computed(() => {
  const raw = localAttendees.value;
  const currentUserId = userDetails.value?.id;
  if (!currentUserId) return raw;
  const idx = raw.findIndex((a) => a?.id === currentUserId);
  if (idx <= 0) return raw;
  return [raw[idx], ...raw.slice(0, idx), ...raw.slice(idx + 1)];
});

interface GambitPlayerSlot {
  player: (typeof localAttendees)['value'][number] | null;
  isInvited?: boolean;
}

type AnyGambitInvitation = typeof props.gambit.invitations extends (infer T)[] | undefined ? T : never;

function getInvitationOwnerId(invitation: AnyGambitInvitation): string | null {
  if ('invitationOwnerId' in invitation) return (invitation as { invitationOwnerId: string | null }).invitationOwnerId;
  if ('ownerId' in invitation) return (invitation as { ownerId: string | null }).ownerId;
  return null;
}

function getInvitationUserId(invitation: AnyGambitInvitation): string | null {
  if ('userId' in invitation && typeof invitation.userId === 'string') return invitation.userId;
  return null;
}

const myGambitInviteeCount = computed(() => {
  const currentUserId = userDetails.value?.id;
  if (!currentUserId) return 0;

  const attendeeIds = new Set(localAttendees.value.map((a) => a?.id).filter(Boolean));
  let count = 0;

  for (const invitation of localInvitations.value) {
    if (invitation.withdrawn === true || invitation.accepted === false) continue;
    if (getInvitationOwnerId(invitation) !== currentUserId) continue;
    const userId = getInvitationUserId(invitation);
    if (!userId || attendeeIds.has(userId)) continue;
    count++;
  }

  return count;
});

const myGambitInvitees = computed((): GambitPlayerSlot[] => {
  const currentUserId = userDetails.value?.id;
  if (!currentUserId) return [];

  const attendeeIds = new Set(localAttendees.value.map((a) => a?.id).filter(Boolean));
  const slots: GambitPlayerSlot[] = [];

  for (const invitation of localInvitations.value) {
    if (invitation.withdrawn === true || invitation.accepted === false) continue;
    if (getInvitationOwnerId(invitation) !== currentUserId) continue;
    const userId = getInvitationUserId(invitation);
    if (!userId || attendeeIds.has(userId)) continue;
    if (!('user' in invitation) || !invitation.user) continue;

    const invUser = invitation.user;
    slots.push({
      player: {
        id: invUser.id,
        username: invUser.username,
        rating: invUser.rating,
        ratingDisplayMode: invUser.ratingDisplayMode,
        profileImageBlobHash: invUser.profileImageBlobHash ?? null,
      },
      isInvited: true,
    });
  }

  return slots;
});

const playerSlots = computed((): GambitPlayerSlot[] => {
  if (isExpired.value) {
    const exlots: GambitPlayerSlot[] = [];

    for (let i = 0; i < avatarsCount.value; i++) {
      exlots.push({ player: null });
    }

    return exlots;
  }

  const slots: GambitPlayerSlot[] = [];
  const attendees = prioritizedAttendees.value;

  if (props.gambit.isChallenge && !props.gambit.isConfirmed) {
    for (const attendee of attendees.slice(0, 2)) {
      if (attendee) slots.push({ player: attendee });
    }

    if (slots.length < 2) {
      const slotIds = new Set(slots.map((s) => s.player?.id).filter(Boolean));
      const challengeInvitation = localInvitations.value.find(
        (inv) =>
          inv.type === GambitInvitationType.CHALLENGE &&
          'user' in inv &&
          inv.user &&
          inv.userId &&
          !slotIds.has(inv.userId),
      );

      if (challengeInvitation && 'user' in challengeInvitation && challengeInvitation.user) {
        const invUser = challengeInvitation.user;
        slots.push({
          player: {
            username: invUser.username,
            profileImageBlobHash: invUser.profileImageBlobHash ?? null,
            rating: invUser.rating,
            ratingDisplayMode: invUser.ratingDisplayMode,
            id: invUser.id,
          },
          isInvited: true,
        });
      }
    }

    while (slots.length < 2) {
      slots.push({ player: null });
    }

    // Challenged, you take the first seat and the challenger the second.
    const myIndex = slots.findIndex((slot) => !!userDetails.value?.id && slot.player?.id === userDetails.value.id);
    if (myIndex > 0) slots.unshift(...slots.splice(myIndex, 1));

    return slots;
  }

  const maxPlayerSlots = avatarsCount.value - 1;

  for (let s = 0; s < maxPlayerSlots; s++) {
    const attendee = attendees[s];
    if (attendee) {
      slots.push({ player: attendee });
    } else {
      break;
    }
  }

  if (hasPendingRequestInvitation.value && userDetails.value && slots.length < maxPlayerSlots) {
    const alreadyInSlots = slots.some((s) => s.player?.id === userDetails.value!.id);
    if (!alreadyInSlots) {
      slots.unshift({
        player: {
          id: userDetails.value.id,
          username: userDetails.value.username || '',
          rating: userDetails.value.rating ?? 0,
          profileImageBlobHash: userDetails.value.profileImageBlobHash ?? null,
        },
        isInvited: true,
      });
    }
  }

  const remaining = maxPlayerSlots - slots.length;
  if (remaining > 0) {
    for (const invSlot of myGambitInvitees.value.slice(0, remaining)) {
      slots.push(invSlot);
    }
  }

  while (slots.length < maxPlayerSlots) {
    slots.push({ player: null });
  }

  slots.push({ player: null });

  return slots;
});

const isUnconfirmedChallenge = computed(() => {
  return props.gambit.isChallenge && !props.gambit.isConfirmed;
});

/* The challenger waits for the other player; withdrawing stays on the gambit detail so it is not one tap away. */
const isSentChallenge = computed(() => isUnconfirmedChallenge.value && localIsAttending.value);

const waitingForLabel = computed((): string => {
  const currentUserId = userDetails.value?.id;
  const invitation = localInvitations.value.find(
    (inv) => inv.type === GambitInvitationType.CHALLENGE && getInvitationUserId(inv) !== currentUserId && 'user' in inv,
  );
  const username = invitation && 'user' in invitation ? invitation.user?.username : null;

  return username ? localizeWithValues('gambit.challenge.waiting_for', [username]) : localize('gambit.challenge.sent');
});

const isConfirmedStatus = computed(() => props.gambit.isConfirmed && !hasPendingRequestInvitation.value);

const statusLabel = computed((): string | null => {
  if (isExpired.value || hasGambitConcluded.value) return null;
  if (props.gambit.isChallenge && !props.gambit.isConfirmed) {
    if (props.gambit.isEditedChallenge) return localize('gambit.challenge.edited');
    return isSentChallenge.value ? localize('gambit.challenge.sent') : 'Challenge';
  }
  if (hasPendingRequestInvitation.value) return 'Invitation';
  if (isConfirmedStatus.value) return 'Confirmed';
  return `${props.gambit.attendeeCount} ${pluralize('attendee', 'attendees', props.gambit.attendeeCount)}`;
});

const confirmedAttendeeFaces = computed(() => prioritizedAttendees.value.filter((attendee) => !!attendee));

/* A confirmed gambit is a group, so its faces pile up. Proposals, unconfirmed
   challenges and pending invitations keep the spaced empty seats. */
const isAttendeeStack = computed(() => isConfirmedStatus.value && !isExpired.value);

const stackedAttendees = computed(() => confirmedAttendeeFaces.value.slice(0, 4));

const stackOverflowCount = computed(() => Math.max(0, confirmedAttendeeFaces.value.length - 4));

const gambitRatingTag = computed((): GamChipType | null => {
  const ratedLocals = localAttendees.value
    .filter((attendee): attendee is NonNullable<(typeof localAttendees.value)[number]> => !!attendee)
    .filter((attendee) => typeof attendee.rating === 'number' && attendee.rating > 0);

  const bounds = [props.gambit.minEloRating, props.gambit.maxEloRating].filter(
    (rating): rating is number => typeof rating === 'number' && rating > 0,
  );
  const values = [...ratedLocals.map((attendee) => attendee.rating as number), ...bounds];
  if (!values.length) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const presentation =
    ratedLocals.length === 1 && min === max
      ? PlayerHelper.groupRatingPresentation(ratedLocals)
      : { label: min === max ? String(min) : `${min} - ${max}` };

  if (!presentation) return null;

  return {
    ...presentation,
    size: GamChipSize.X_SMALL,
    leftIcon: GamIconName.GAMBIT,
  };
});

const unpairedChip = useUnpairedChip(() => localAttendees.value.map((attendee) => attendee?.id));

const gambitBandTags = computed((): GamChipType[] => {
  const isAttending = !!localIsAttending.value;
  const attendeeCount = Math.max(props.gambit.attendeeCount || 0, localAttendees.value.filter(Boolean).length);
  /* Faces already show it's just you, so the chip says YOU rather than "Only you". */
  const isOnlyMe = isAttending && attendeeCount <= 1;

  const youChip: GamChipType | null = isOnlyMe
    ? {
        label: 'You',
        size: GamChipSize.X_SMALL,
        leftIcon: GamIconName.PAWN,
        variant: GamChipVariant.PRIMARY,
        isActive: true,
        isUpperCase: true,
      }
    : null;

  const [playerTags, typeTags] = [true, false].map((isPlayers) =>
    GambitHelper.getGambitsTags(
      { ...(props.gambit as unknown as BaseGambitDto), isAttending, attendeeCount },
      {
        size: GamChipSize.X_SMALL,
        showAttendees: isPlayers && !isOnlyMe,
        showRating: false,
        showCategory: false,
        showTypes: !isPlayers,
        showChessSets: false,
        showRecurring: false,
        highlightAttendance: true,
      },
    ).map((tag) => (tag.variant === GamChipVariant.PRIMARY ? { ...tag, isActive: true } : tag)),
  );

  return [
    ...(unpairedChip.value ? [unpairedChip.value] : []),
    ...(youChip ? [youChip] : []),
    ...playerTags,
    ...(gambitRatingTag.value ? [gambitRatingTag.value] : []),
    ...typeTags,
  ];
});

const hasPendingRequestInvitation = computed(() => {
  const currentUserId = userDetails.value?.id;

  if (!currentUserId) {
    return false;
  }

  return localInvitations.value.some((invitation) => {
    if (!('userId' in invitation) || invitation.userId !== currentUserId) {
      return false;
    }

    if (invitation.type !== GambitInvitationType.REQUEST) {
      return false;
    }

    const accepted = 'accepted' in invitation ? invitation.accepted : undefined;
    const withdrawn = 'withdrawn' in invitation ? invitation.withdrawn : undefined;

    return accepted !== true && accepted !== false && withdrawn !== true;
  });
});

/** An incoming challenge or invitation, not one this player already sent. */
const needsRespond = computed(
  () => (isUnconfirmedChallenge.value && !isSentChallenge.value) || hasPendingRequestInvitation.value,
);

// Actions

const handleNavigateClick = () => {
  LocationHelper.redirectToGoogleMaps(props.gambit.location?.coordinates);
};

const handleLocationDetailsClick = () => {
  const location = props.gambit.location;
  if (!location?.id) return;

  useMapStore().setRingedSpot(location.id);
  router.push({
    name: RouterNameType.LOCATIONS,
    query: {
      lat: location.coordinates?.lat,
      lng: location.coordinates?.lng,
      zoom: '15',
    },
  });
};

const handleShareGambit = async () => {
  const url = `${window.location.origin}/share/gambit/${props.gambit.id}`;
  const locationName = props.gambit.location?.name || 'Chess';
  const tz = props.gambit.timezone || 'UTC';

  const todayDate = isToday(props.gambit.start);
  const tomorrowDate = isTomorrow(props.gambit.start);
  const day = todayDate ? 'Today' : tomorrowDate ? 'Tomorrow' : formatInTimeZone(props.gambit.start, tz, 'EEEE');
  const time = formatInTimeZone(props.gambit.start, tz, 'HH:mm');
  const title = `Chess at ${locationName} — ${day} ${time}`;
  const text = `${props.gambit.attendeeCount} going. Join now!`;

  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return;
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
    }
  }

  try {
    await navigator.clipboard.writeText(url);
  } catch {
    // clipboard write failed silently
  }
};

const handleOpenGambit = async () => {
  if (!props.gambit) return;

  const backRoute = { ...route, query: { ...route.query, view: `gambit:${props.gambit.id}` } };
  const backUrl = router.resolve(backRoute).href;

  router.push({ name: RouterNameType.GAMBIT, params: { id: props.gambit.id }, query: { back: backUrl } });
};

const handleNavigateToClub = () => {
  if (!props.gambit.club?.username) return;

  router.push({
    name: RouterNameType.USERNAME,
    params: { username: props.gambit.club.username },
    query: { back: 'history' },
  });
};

const handleNavigateToPlayerProfile = (username?: string) => {
  if (!username) return;

  router.push({ name: RouterNameType.USERNAME, params: { username }, query: { back: 'history' } });
};

const playerChessSets = computed(() => {
  const fromAttendees = localAttendees.value.reduce((sum, attendee) => sum + (attendee?.chessSetCount ?? 0), 0);
  if (fromAttendees > 0) return fromAttendees;
  return (
    (props.gambit as GambitDetailDto).attendeeChessSetCount ||
    (props.gambit as GambitSearchResultDto).attendeeChessCount ||
    0
  );
});

const spotChessSets = computed(() => {
  const location = props.gambit.location;
  const features = location?.chessFeatures || [];
  if (features.length > 0 && !features.includes(LocationChessFeature.CHESS_SET)) return 0;
  const searchLocationCount = (props.gambit as GambitSearchResultDto).locationChessSetCount;
  if (typeof searchLocationCount === 'number') return searchLocationCount;
  return location?.chessSetCount || 0;
});

const handleAvatarClick = (player: { id?: string; username?: string } | null | undefined) => {
  if (!player) return;

  handleNavigateToPlayerProfile(player.username);
};

/* Chess sets live on their own control rather than on the avatar, so avatars are
   only ever profile links. Editing requires an attendance row to patch. */
const showChessSetAction = computed(() => localIsAttending.value && !isExpired.value && !hasGambitConcluded.value);

const myChessSetCount = computed(
  () => localAttendees.value.find((a) => a?.id === userDetails.value?.id)?.chessSetCount ?? 0,
);

/** Fewer boards than one per pair of attendees, counting the spot's own. Same read as the gambit overlay. */
const isShortOfChessSets = computed(
  () => playerChessSets.value + spotChessSets.value < Math.ceil((props.gambit.attendeeCount || 0) / 2),
);

const handleOpenChessSetPopover = () => {
  editChessSetCount.value = myChessSetCount.value;
  showChessSetPopover.value = true;
};

const handleConfirmChessSet = async (count: number) => {
  isUpdatingChessSet.value = true;
  try {
    await gambitStore.updateAttendance(props.gambit.id, count);
    const meIndex = localAttendees.value.findIndex((a) => a?.id === userDetails.value?.id);
    if (meIndex >= 0 && localAttendees.value[meIndex]) {
      localAttendees.value[meIndex] = {
        ...localAttendees.value[meIndex]!,
        chessSetCount: count,
      };
    }
    showChessSetPopover.value = false;
  } finally {
    isUpdatingChessSet.value = false;
  }
};

async function handleCloseInvitePlayers() {
  closeInvitePlayers();
  const result = await gambits.gambitService.getGambit(props.gambit.id);
  if (result.data) {
    if (result.data.attendees?.length) {
      localAttendees.value = [...result.data.attendees];
    }
    if (result.data.invitations) {
      localInvitations.value = [...result.data.invitations];
    }
  }
}

async function handleJoinAndInvite(playerIds: string[]) {
  isSubmitting.value = true;
  try {
    await handleJoinGambit();

    if (playerIds.length) {
      const result = await gambits.gambitService.createInvitations(
        props.gambit.id,
        playerIds,
        GambitInvitationType.REQUEST,
      );
      if (result.error) {
        useToast().error(result.error.message);
      } else {
        useToast().info(`${playerIds.length > 1 ? 'Players' : 'Player'} invited.`);
      }
    }

    const refreshed = await gambits.gambitService.getGambit(props.gambit.id);
    if (refreshed.data) {
      if (refreshed.data.attendees?.length) {
        localAttendees.value = [...refreshed.data.attendees];
      }
      if (refreshed.data.invitations) {
        localInvitations.value = [...refreshed.data.invitations];
      }
    }
  } finally {
    isSubmitting.value = false;
  }
}

function handleOpenInvitePlayers() {
  if (isConcluded.value || isExpired.value) return;

  const mode = localIsAttending.value ? 'send' : 'join-and-invite';

  patchInvitePlayersOptions({
    attrs: {
      ...getInviteModalAttrs(),
      mode,
      onJoinAndInvite: mode === 'join-and-invite' ? handleJoinAndInvite : undefined,
    },
  });

  openInvitePlayers();
}

const emit = defineEmits<(e: 'gambit-joined' | 'gambit-left', gambitId: string) => void>();

async function handleJoinGambit() {
  if (!props.gambit) return;

  isSubmitting.value = true;
  localIsAttending.value = true;

  if (userDetails.value) {
    const me = {
      id: userDetails.value.id,
      username: userDetails.value.username,
      rating: userDetails.value.rating ?? 0,
      ratingDisplayMode: userDetails.value.ratingDisplayMode,
      profileImageBlobHash: userDetails.value.profileImageBlobHash ?? null,
      firstName: '',
      lastName: '',
      emoji: '',
      chessSetCount: 0,
      isBlocked: false,
      isConfirmed: true,
      isOrganizer: false,
      initiatedHandshakes: [],
      receivedHandshakes: [],
    };
    if (!localAttendees.value.some((a) => a?.id === me.id)) {
      localAttendees.value = [...localAttendees.value, me];
    }
  }

  if (isUnconfirmedChallenge.value) {
    const outcome = await gambitStore.acceptChallenge(props.gambit.id);
    isSubmitting.value = false;
    if (outcome.merged) {
      router.push({
        name: RouterNameType.GAMBIT,
        params: { id: outcome.gambitId },
        query: { back: 'history' },
      });
      return;
    }
  } else {
    await gambitStore.joinGambit(props.gambit.id);
  }

  isSubmitting.value = false;
  emit('gambit-joined', props.gambit.id);
}

const { isLeaveConfirmOpen, leaveConfirmTitle, requestLeaveGambit, dismissLeaveConfirm, confirmLeaveGambit } =
  useLeaveGambitConfirm();

const handleLeaveGambit = async () => {
  if (!props.gambit) return;
  await requestLeaveGambit(props.gambit, performLeaveGambit);
};

const performLeaveGambit = async () => {
  if (!props.gambit) return;

  isSubmitting.value = true;
  localIsAttending.value = false;

  if (userDetails.value) {
    localAttendees.value = localAttendees.value.filter((a) => a?.id !== userDetails.value!.id);
  }

  await gambitStore.leaveGambit(props.gambit.id);

  isSubmitting.value = false;
  emit('gambit-left', props.gambit.id);
};

const handleImageLoaded = () => {
  isImageLoaded.value = true;
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.GambitCard {
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: var(--card-stack-gap);
  background: transparent;
  overflow: visible;
}

.GambitCard.spark-card {
  box-shadow: none;
}

.GambitCard.is-expired {
  filter: saturate(0);
}

.cover {
  position: relative;
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
  border-radius: var(--radius-normal);
  overflow: hidden;
  background: var(--color-dark-600);
  cursor: pointer;
}

.cover-foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem var(--card-gutter) var(--card-foot-inset);
  background: linear-gradient(transparent 0%, rgba(28, 28, 28, 0.75) 100%);
  pointer-events: none;
}

.cover-foot > * {
  pointer-events: auto;
}

/* Bottom-aligned: a filled seat carries a badge row above its face, so centring
   would drop that face below the empty rings beside it. */
.cover-players {
  display: flex;
  align-items: flex-end;
  gap: clamp(0.25rem, min(1.75cqw, 1.17cqh), 0.5rem);
}

.cover-players.is-attendee-stack {
  gap: clamp(1.25rem, min(6cqw, 4cqh), 1.75rem);
}

.attendee-pile {
  display: flex;
  align-items: center;
}

.cover-players.is-attendee-stack .seat.is-piled,
.cover-players.is-attendee-stack .seat.is-invite-apart {
  width: var(--card-avatar-size);
  min-height: var(--card-avatar-size);
  flex: none;
}

.cover-players.is-attendee-stack .seat.is-piled + .seat.is-piled {
  margin-left: calc(var(--card-avatar-size) * -0.28);
}

.seat.is-piled .seat-ring.is-more {
  border: 1.5px solid var(--color-white-60);
}

.seat {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;
  width: var(--card-avatar-size);
  min-height: 0;
}

.seat.is-unconfirmed {
  opacity: 0.4;
}

.cover-players :deep(.PlayerAvatar) {
  cursor: pointer;
}

/* Grey faces vanish into the cover photo. The ring lifts them off dark imagery
   and the shadow off light imagery; kept in sync with SparkCard and the week card. */
.cover-players :deep(.PlayerAvatar:not(.is-invited) .avatar) {
  box-shadow:
    0 0 0 1.5px var(--color-white-60),
    0 6px 16px rgb(0 0 0 / 0.55);
}

.seat-ring {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-white-20);
  color: var(--color-white-40);
}

.seat-ring.is-invite {
  border-style: dashed;
  border-color: var(--color-white-30);
  color: var(--color-white-60);
  cursor: pointer;
  transition: color 0.15s ease;
}
@include hover-capable {
  .seat-ring.is-invite:hover {
    color: var(--color-white);
  }
}

.seat-ring.is-more {
  background: var(--color-white-5);
  cursor: pointer;
}

.seat-count {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.seat-ring.is-more .seat-count {
  font-size: 18px;
}

.cover-actions {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  z-index: 6;
}

.meta {
  flex: none;
  height: max(var(--card-meta-min), calc(50cqw - var(--card-stack-gap)));
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(0.4rem, min(2.5cqw, 1.67cqh), 0.75rem);
  padding: var(--card-gutter);
  border-radius: var(--radius-normal);
  background: var(--color-card-surface);

  &.needs-respond {
    border: 2px solid var(--color-pink);
  }
}

.meta-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.meta-head-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.info-action {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  color: var(--color-white-80);
  cursor: pointer;
  transition: color 200ms linear;

  > span {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.03em;
  }

  &:active {
    color: var(--color-white);
  }

  @include hover {
    color: var(--color-white);
  }
}

.info-icon {
  position: relative;
  display: flex;
}

.unread-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 18px;
  background-color: var(--color-pink);
  color: var(--color-dark-700);
  pointer-events: none;
}

.when {
  display: flex;
  align-items: baseline;
  gap: 0.3em;
  font-size: clamp(1.05rem, min(6cqw, 4cqh), 1.5rem);
  letter-spacing: 0.02em;
  color: var(--color-white);
  white-space: nowrap;
}

.when .day-live {
  color: var(--color-pink);
  font-weight: 600;
}

.sub {
  display: flex;
  align-items: baseline;
  gap: 0.35em;
  min-width: 0;
  font-size: clamp(0.75rem, min(4cqw, 2.67cqh), 0.875rem);
  letter-spacing: 0.02em;
  color: var(--color-white-50);
}

.conflict-warning {
  display: flex;
  align-items: center;
  gap: 0.3em;
  min-width: 0;
  font-size: clamp(0.7rem, min(3.6cqw, 2.4cqh), 0.8rem);
  color: var(--color-orange);

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.sub .sep,
.when .sep {
  color: var(--color-white-30);
  flex: none;
}

.sub .spot {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.sub .spot.confirmed {
  color: var(--color-white);
}

.sub .status {
  color: var(--color-orange);
  font-weight: 600;
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.sub .status.confirmed {
  color: var(--color-green);

  :deep(svg path) {
    stroke: currentColor;
    stroke-width: 1px;
  }
}

.controls {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

.tertiary,
.primary,
.secondary {
  display: flex;
  align-items: center;
}

.tertiary {
  grid-column: 1;
  justify-self: start;
  min-width: 0;
}

/* Mirrors the chess-set counter's value field used elsewhere in the deck. */
/* Matches the gambit overlay's secondary action button, scaled to card units. */
.chess-set-action {
  width: var(--card-meet-size);
  height: var(--card-meet-size);
  @include toolbar-action;
}

.tertiary :deep(.svg-container) {
  font-size: var(--card-meet-icon) !important;
  width: var(--card-meet-icon) !important;
  height: var(--card-meet-icon) !important;
  min-width: var(--card-meet-icon) !important;
}

.primary {
  grid-column: 2;
  justify-self: center;
}

.secondary {
  grid-column: 3;
  justify-self: start;
  padding-left: clamp(0.75rem, min(4cqw, 2.67cqh), 1.25rem);
}

.primary :deep(.gam-button-container.is-icon) {
  width: var(--card-play-size) !important;
  height: var(--card-play-size) !important;
  min-width: var(--card-play-size) !important;
  min-height: var(--card-play-size) !important;
}

.primary :deep(.gam-button-container:not(.is-icon)) {
  height: var(--card-play-size) !important;
  min-height: var(--card-play-size) !important;
}

.primary :deep(.svg-container) {
  font-size: var(--card-play-icon) !important;
  width: var(--card-play-icon) !important;
  height: var(--card-play-icon) !important;
  min-width: var(--card-play-icon) !important;
}

.expired-notice {
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-white-50);
}

.location-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  mask-image: linear-gradient(black 0%, black 75%, transparent 100%);
}

.location-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
}

.image-loaded .location-image img {
  opacity: 1;
  transition: opacity var(--transition);
}

/* The static map is centred on the coordinates, and object-fit keeps that pixel at
   the cover's centre. Anchor the circle itself there; its name hangs below. */
.spot-marker {
  --circle-size: calc(var(--card-avatar-size, 3rem) * 0.8);
  --ring-offset: 4px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, calc(-0.5 * (var(--circle-size) + 2 * var(--ring-offset))));
  z-index: 1;
  pointer-events: none;
}

.spot-marker :deep(.LocationCircle) {
  --circle-size: calc(var(--card-avatar-size, 3rem) * 0.8);
  --ring-offset: 4px;
  width: auto;
}

/* One line, unlike the two the circle allows elsewhere: a wrapped name would
   grow the marker and eat the clearance the offsets above are tuned for. */
.spot-marker :deep(.circle-name) {
  max-width: 60cqw;
  font-size: clamp(0.6875rem, min(3.6cqw, 2.4cqh), 0.8125rem);
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.icon-circle :deep(.gam-button-container) {
  border-radius: 50% !important;

  .icon {
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
  }
}

@include play-cta;

.card-action {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white-30);
  transition: color 0.15s ease;
}
@include hover-capable {
  .card-action:hover {
    color: var(--color-white-70);
  }
}

.location-actions-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(1.25rem, 8%, 2.5rem);
  padding: 0 1rem;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
}

.location-actions-enter-active,
.location-actions-leave-active {
  transition: opacity 0.15s ease;
}

.location-actions-enter-from,
.location-actions-leave-to {
  opacity: 0;
}

.location-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  color: var(--color-white-80);
  cursor: pointer;

  span {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.03em;
  }

  &:active {
    color: var(--color-white);
  }
}

.chip-band {
  flex: none;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-xs);
  height: var(--card-band-height);
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }
}

.band-title {
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: var(--spacing-xxs);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-white);
}

.sub .club-link {
  color: var(--color-white-50);
  cursor: pointer;
  white-space: nowrap;
  flex: none;
}
@include hover-capable {
  .sub .club-link:hover {
    text-decoration: underline;
  }
}
</style>
